import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Stack, Switch, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React from 'react';

import SkillActionsMenu from 'llm/chat-library/skills/SkillActionsMenu';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';

interface SkillsListProps {
  skills: Array<SkillSummary>;
  onArchive: (skillId: string, reason: string) => void;
  onEnable: (skillId: string) => void;
  onDisable: (skillId: string) => void;
  selectedSkillId?: string | null;
  onSelectSkill: (skill: SkillSummary) => void;
  onManageAccess: (skill: SkillSummary) => void;
}

const useListStyles = createStyles(() => ({
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

const useCardStyles = createStyles((theme, {isSelected}: {isSelected: boolean}) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} 10px`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${isSelected ? theme.colors.blue[4] : theme.colors.gray[3]}`,
    backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
    width: '100%',
    textAlign: 'left',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: isSelected ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexWrap: 'wrap',
  },
  description: {
    marginTop: 2,
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    marginTop: 2,
  },
}));

function SkillCard({
  skill,
  onArchive,
  onToggleEnabled,
  isSelected,
  onSelect,
  onManageAccess,
}: {
  skill: SkillSummary;
  onArchive: (skillId: string, reason: string) => void;
  onToggleEnabled: (skill: SkillSummary, enabled: boolean) => void;
  isSelected: boolean;
  onSelect: (skill: SkillSummary) => void;
  onManageAccess: (skill: SkillSummary) => void;
}): JSX.Element {
  const {classes} = useCardStyles({isSelected});

  return (
    <UnstyledButton
      component="div"
      role="button"
      tabIndex={0}
      className={classes.card}
      aria-label={`View details for ${skill.name}`}
      aria-pressed={isSelected}
      onClick={() => {
        onSelect(skill);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(skill);
        }
      }}
    >
      <div className={classes.cardHeader}>
        <div className={classes.cardContent}>
          <div className={classes.nameRow}>
            <Text fw="bold" size="sm" truncate={true}>
              {skill.name}
            </Text>
          </div>
          {skill.creatorName && (
            <Text size="xs" c="dimmed">
              by {skill.creatorName}
            </Text>
          )}
          <Text size="xs" c="dimmed" lineClamp={2} className={classes.description}>
            {skill.description}
          </Text>
        </div>
        <div
          className={classes.actionsContainer}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
        >
          <Tooltip
            label={
              skill.isEnabled
                ? 'Disable skill (agents will not use it in your sessions)'
                : 'Enable skill (agents will use it in your sessions)'
            }
            withinPortal={true}
          >
            <Switch
              checked={skill.isEnabled}
              size="xs"
              aria-label={skill.isEnabled ? `Disable ${skill.name}` : `Enable ${skill.name}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onToggleEnabled(skill, e.currentTarget.checked);
              }}
              {...getTestProps({component: 'SkillsList', element: 'toggleSkillEnabled'})}
            />
          </Tooltip>
          <SkillActionsMenu
            skill={skill}
            testComponentName="SkillsList"
            onArchive={onArchive}
            onManageAccess={onManageAccess}
          />
        </div>
      </div>
    </UnstyledButton>
  );
}

export default function SkillsList({
  skills,
  onArchive,
  onEnable,
  onDisable,
  selectedSkillId,
  onSelectSkill,
  onManageAccess,
}: SkillsListProps): JSX.Element {
  const {classes} = useListStyles();

  if (skills.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No skills available yet.
      </Text>
    );
  }

  function handleToggleEnabled(skill: SkillSummary, enabled: boolean): void {
    if (enabled) {
      onEnable(skill.id);
    } else {
      onDisable(skill.id);
    }
  }

  function handleSelectSkill(skill: SkillSummary): void {
    onSelectSkill(skill);
  }

  return (
    <Stack spacing="md" className={classes.list}>
      {skills.map((skill) => (
        <SkillCard
          key={skill.id}
          skill={skill}
          onArchive={onArchive}
          onToggleEnabled={handleToggleEnabled}
          isSelected={selectedSkillId === skill.id}
          onSelect={handleSelectSkill}
          onManageAccess={onManageAccess}
        />
      ))}
    </Stack>
  );
}
